import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, Package, Handshake, Trash2, MapPin, Building2 } from "lucide-react";

const TYPES = [
  { v: "propose", label: "Je propose", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { v: "cherche", label: "Je cherche", cls: "bg-sky-50 text-sky-700 border-sky-200" },
];

export default function ProNetworkListings() {
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState([]);
  const [categories, setCategories] = useState([]);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "propose", title: "", description: "", category_id: "", city: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    const p = new URLSearchParams();
    if (type) p.set("type", type); if (q) p.set("q", q);
    api.get(`/pro/network/listings?${p.toString()}`).then((r) => setItems(r.data.items || [])).catch(() => {});
    api.get("/pro/network/listings/mine").then((r) => setMine(r.data.items || [])).catch(() => {});
  };
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data?.categories || r.data || [])).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [type, q]);

  const submit = async () => {
    if (form.title.trim().length < 4) { toast.error("Titre trop court"); return; }
    setSaving(true);
    try {
      await api.post("/pro/network/listings", form);
      toast.success("Annonce publiée 📣");
      setOpen(false); setForm({ type: "propose", title: "", description: "", category_id: "", city: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/pro/network/listings/${id}`); toast.success("Annonce supprimée"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const Tag = ({ t }) => {
    const def = TYPES.find((x) => x.v === t) || TYPES[0];
    return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${def.cls}`}>{def.label}</span>;
  };
  const Card = ({ l, owned }) => (
    <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`listing-card-${l.id}`}>
      <div className="flex items-center justify-between gap-2">
        <Tag t={l.type} />
        {owned && <button onClick={() => remove(l.id)} className="text-slate-400 hover:text-rose-500" data-testid={`listing-delete-${l.id}`}><Trash2 size={15} /></button>}
      </div>
      <div className="font-semibold text-slate-900 mt-2">{l.title}</div>
      {l.description && <p className="text-sm text-slate-600 mt-1 line-clamp-3">{l.description}</p>}
      <div className="text-xs text-slate-500 mt-2 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1"><Building2 size={11} />{l.business?.name}</span>
        {l.city && <span className="inline-flex items-center gap-1"><MapPin size={11} />{l.city}</span>}
        {l.category_name && <span>· {l.category_name}</span>}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="pro-network-listings">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Annonces B2B</h1>
          <p className="text-slate-500 text-sm mt-1">Publiez vos besoins et offres entre pros : fournisseurs, sous-traitance, collaborations.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="listing-new-btn"><Plus size={16} className="mr-1" />Publier une annonce</Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une annonce…" className="pl-9" data-testid="listing-search" />
        </div>
        <div className="inline-flex rounded-lg bg-slate-100 p-0.5" data-testid="listing-type-filter">
          {[["", "Toutes"], ["propose", "Je propose"], ["cherche", "Je cherche"]].map(([v, lbl]) => (
            <button key={v} onClick={() => setType(v)} data-testid={`listing-filter-${v || "all"}`} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${type === v ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>{lbl}</button>
          ))}
        </div>
      </div>

      {mine.length > 0 && (
        <section>
          <h2 className="font-semibold text-slate-800 mb-2 inline-flex items-center gap-2"><Handshake size={16} className="text-[#E05638]" />Mes annonces ({mine.length})</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{mine.map((l) => <Card key={l.id} l={l} owned />)}</div>
        </section>
      )}

      <section>
        <h2 className="font-semibold text-slate-800 mb-2 inline-flex items-center gap-2"><Package size={16} className="text-sky-600" />Toutes les annonces du réseau</h2>
        {items.length === 0 ? (
          <div className="text-sm text-slate-400 bg-slate-50 rounded-xl p-4">{(type || q) ? "Aucun résultat pour votre recherche." : "Aucune annonce pour le moment. Soyez le premier à publier !"}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{items.map((l) => <Card key={l.id} l={l} owned={mine.some((m) => m.id === l.id)} />)}</div>
        )}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="listing-dialog">
          <DialogHeader><DialogTitle>Publier une annonce B2B</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
              {TYPES.map((t) => (
                <button key={t.v} onClick={() => setForm({ ...form, type: t.v })} className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${form.type === t.v ? "bg-white shadow text-slate-900" : "text-slate-500"}`} data-testid={`listing-type-${t.v}`}>{t.label}</button>
              ))}
            </div>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre (ex. Cherche graphiste freelance)" data-testid="listing-title" />
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} placeholder="Décrivez votre besoin ou votre offre…" data-testid="listing-desc" />
            <div className="flex gap-2">
              <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="h-10 flex-1 rounded-md border border-slate-200 px-3 text-sm" data-testid="listing-category">
                <option value="">Secteur (optionnel)</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ville" className="w-40" data-testid="listing-city" />
            </div>
            <Button onClick={submit} disabled={saving} className="w-full bg-[#E05638] hover:bg-[#c94a2f]" data-testid="listing-submit">{saving ? "Publication…" : "Publier"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
